import type { ReportingFilterOptionsInput } from '@risksmart-app/shared/reporting/api/schema';
import type { ReportingFilterOptionsResponse } from '@risksmart-app/trpc/src/types';
import type { GetReportingFilterOptionsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetReportingFilterOptionsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createLazyQueryHook } from 'src/utils';

import { toIsoIfDate } from './mapReportingData';

/**
 * Imperative `getReportingFilterOptions` read (RSP-6504) — the property-filter
 * panel fetches suggestion values on demand as the user types/pages.
 *
 * The tRPC procedure types each option value as `unknown`, but the legacy
 * GraphQL contract (`ReportingFilterOption.value: String!`) is non-null string
 * and the property-filter UI consumes strings, so coerce to keep the mapped
 * shape identical to the generated query type. Date values go through
 * `toIsoIfDate` first: plain `String(date)` yields the locale form
 * ("Fri Jul 24 2026 …"), whereas V2 — which JSON-serialised the action
 * response — sent an ISO string, and that string is what the chosen suggestion
 * is sent back to the server as in the filter token.
 */
export const useLazyGetReportingFilterOptions = createLazyQueryHook<
  ReportingFilterOptionsInput,
  ReportingFilterOptionsResponse,
  GetReportingFilterOptionsQuery
>({
  trpcQuery: (client, args) =>
    client.frontend.reporting.reportingFilterOptions.query(args),
  mapTrpcDataToGraphQL: (data) => ({
    reportingFilterOptions: data.map((option) => ({
      value: String(toIsoIfDate(option.value)),
    })),
  }),
  graphqlDocument: GetReportingFilterOptionsDocument,
  graphqlVariables: (args) => ({ Input: args }),
  // The property-filter panel sets its own `error` status from the returned
  // result (see CustomDatasourcePropertyFilter), so the factory's raw-message
  // toast would be a second notification for one failure — and it would stack
  // on every keystroke while the error is set.
  suppressErrorNotification: true,
});
